import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { FbLink } from 'components/FbUI/FbLink'
import { ActivityV2 } from 'models/activityV2'
import { ActivityEventCell } from './CellEvent/ActivityEventCell'
import { TwoLineDateCell } from 'components/Tables/CommonTableCells/TwoLineDateCell'
import { InitialsAvatar } from 'components/Tables/CommonTableCells/InitialsAvatar'
import { formatInteger } from 'utils/formatting'
import { contentTypeMap } from './utils'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { ACTIVITY_V2_COLUMNS } from 'components/DataExpansionComponents/Activity/activityV2ColumnInfo'
import { OverflownText } from 'components/OverflownText'
import { Fragment } from 'react'
import { PillOverflowChip } from 'components/Tables/CommonTableCells/PillOverflowChip'

export const useActivityTableColumnsV2 = (): ColumnDef<ActivityV2, any>[] => {
  const columnHelper = createColumnHelper<ActivityV2>()

  const activityColumnHelper = createTableColumnHelper({
    columns: ACTIVITY_V2_COLUMNS,
  })
  return [
    columnHelper.accessor('user_name', {
      ...activityColumnHelper.retrieveColumn('user'),
      size: 220,
      cell: (info) => {
        const name = (info.getValue() as string | null) ?? ''
        if (!name) return <>—</>
        const initials = name
          .split(' ')
          .map((n) => n[0] ?? '')
          .join('')
          .toUpperCase()
          .slice(0, 2)
        return (
          <div className="flex items-center gap-2.5">
            <InitialsAvatar initials={initials} />
            <span className="truncate">{name}</span>
          </div>
        )
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('event'),
      size: 324,
      cell: ({ row }) => <ActivityEventCell event={row.original} />,
    }),
    columnHelper.accessor('bulk_size', {
      ...activityColumnHelper.retrieveColumn('bulk_size'),
      size: 100,
      cell: ({ getValue }) => {
        const value = getValue()
        return formatInteger(value)
      },
    }),
    columnHelper.accessor('content_type', {
      ...activityColumnHelper.retrieveColumn('content_type'),
      cell: ({ row }) => {
        return contentTypeMap[row.original.content_type.model]
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('company'),
      size: 250,
      cell: ({ row }) => {
        const companies = row.original.companies
        if (!companies.length) return null

        // For reminders, bulk_size represents recipient count, not company count
        const isReminder = row.original.content_type.model === 'reminder'
        const companiesNotShown = isReminder
          ? 0
          : Math.max(0, row.original.bulk_size - companies.length)
        const [first, ...rest] = companies
        const overflowCount = rest.length + companiesNotShown

        return (
          <div className="flex items-center gap-1 min-w-0">
            <FbLink
              to={`/company/${first[0]}`}
              target="_blank"
              rel="noreferrer"
              className="min-w-0"
              style={{
                textDecoration: 'none',
                color: '#0A9150',
                fontWeight: 500,
              }}
            >
              <OverflownText tooltipText={first[1]}>{first[1]}</OverflownText>
            </FbLink>
            {overflowCount > 0 && (
              <PillOverflowChip count={overflowCount}>
                <div className="flex flex-col gap-1.5 w-full">
                  {rest.map((company) => (
                    <FbLink
                      key={'activity-co-' + company[0]}
                      to={`/company/${company[0]}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block w-full truncate"
                      style={{
                        textDecoration: 'none',
                        color: '#0A9150',
                        fontWeight: 500,
                      }}
                      title={company[1]}
                    >
                      {company[1]}
                    </FbLink>
                  ))}
                  {companiesNotShown > 0 && (
                    <span className="text-xs italic text-neutral-500">
                      +{companiesNotShown} more not shown
                    </span>
                  )}
                </div>
              </PillOverflowChip>
            )}
          </div>
        )
      },
    }),
    columnHelper.display({
      ...activityColumnHelper.retrieveColumn('door'),
      size: 250,
      cell: ({ row }) => {
        // Show door column for doorproxy, doornote, and reminder content types
        if (
          !['doorproxy', 'doornote', 'reminder'].includes(
            row.original.content_type.model
          ) ||
          !row.original.doors?.length
        ) {
          return null
        }

        if (row.original.bulk_size === 1) {
          return (
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              <FbLink
                to={`/door/UG${row.original.doors[0][0]}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block max-w-full break-words whitespace-normal overflow-visible"
                style={{ textOverflow: 'clip' }}
              >
                {row.original.doors[0][1]}
              </FbLink>
            </div>
          )
        }

        const doorsNotShown =
          row.original.bulk_size - row.original.doors!.length
        const tooltipText =
          row.original.doors!.map((door) => door[1]).join(', ') +
          (doorsNotShown > 0 ? `, ... (${doorsNotShown} more)` : '')

        return (
          <OverflownText tooltipText={tooltipText} maxLines={1}>
            <div className="break-words whitespace-normal max-w-full overflow-hidden">
              {row.original.doors!.map((door, index) => (
                <Fragment key={'doors-link-activity-table' + door[0]}>
                  <FbLink
                    to={`/door/UG${door[0]}`}
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      textOverflow: 'clip',
                      overflow: 'visible',
                    }}
                  >
                    {door[1]}
                    {index < row.original.doors!.length - 1 && <span>,</span>}
                  </FbLink>
                  {index < row.original.doors!.length - 1 && <span> </span>}
                </Fragment>
              ))}
              {doorsNotShown > 0 && ' ...'}
            </div>
          </OverflownText>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      ...activityColumnHelper.retrieveColumn('created_at', {
        // Two-line date/time cell — keep both lines (don't single-line
        // truncate). minWidth keeps the date on one line even at the min.
        meta: { multiLine: true, minWidth: 140 },
      }),
      size: 150,
      cell: ({ row }) => <TwoLineDateCell value={row.original.created_at} />,
    }),
  ]
}
